import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from '../lib/toast';
import { Upload, UserPlus, Plus, X } from 'lucide-react';

interface EmployeeFormProps {
  onSuccess: () => void;
  onCancel: () => void;
  initialData?: {
    id: string;
    name: string;
    email: string;
    job_title: string;
    photo_url: string | null;
    technology?: string[] | string;
    availability?: number;
  };
}

export default function EmployeeForm({ onSuccess, onCancel, initialData }: EmployeeFormProps) {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(initialData?.name || '');
  const [email, setEmail] = useState(initialData?.email || '');
  const [jobTitle, setJobTitle] = useState(initialData?.job_title || '');
  const [expertise, setExpertise] = useState<string[]>(() => {
    if (Array.isArray(initialData?.technology)) {
      return initialData.technology;
    }
    if (typeof initialData?.technology === 'string') {
      try {
        // Try to parse if it's a JSON string array
        const parsed = JSON.parse(initialData.technology);
        return Array.isArray(parsed) ? parsed : [initialData.technology];
      } catch {
        // If not JSON, split by comma if it contains commas
        return initialData.technology.includes(',') 
          ? initialData.technology.split(',').map(t => t.trim())
          : [initialData.technology];
      }
    }
    return [];
  });
  const [availability, setAvailability] = useState(initialData?.availability?.toString() || '40');
  const [error, setError] = useState<string | null>(null);
  const [createAccount, setCreateAccount] = useState(false);
  const [password, setPassword] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(initialData?.photo_url || null);

  const addExpertiseField = () => {
    setExpertise([...expertise, '']);
  };

  const removeExpertiseField = (index: number) => {
    setExpertise(expertise.filter((_, i) => i !== index));
  };

  const updateExpertise = (index: number, value: string) => {
    const newExpertise = [...expertise];
    newExpertise[index] = value;
    setExpertise(newExpertise);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      let photo_url = initialData?.photo_url || null;

      if (photoFile) {
        const fileExt = photoFile.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;

        const { error: uploadError, data } = await supabase.storage
          .from('team-photos')
          .upload(fileName, photoFile);

        if (uploadError) throw uploadError;

        if (data) {
          const { data: { publicUrl } } = supabase.storage
            .from('team-photos')
            .getPublicUrl(fileName);
          
          photo_url = publicUrl;
        }
      }

      if (!initialData) {
        // Create team member
        const { error: memberError } = await supabase
          .from('team_members')
          .insert([{
            admin_id: session.user.id,
            name,
            email,
            job_title: jobTitle,
            technology: expertise.filter(e => e.trim().length > 0).map(e => e.trim()),
            availability: parseInt(availability),
            photo_url
          }]);

        if (memberError) throw memberError;
        toast.success('Team member added successfully');
      } else {
        // Update existing team member
        const { error: updateError } = await supabase
          .from('team_members')
          .update({
            name,
            email,
            job_title: jobTitle,
            technology: expertise.filter(e => e.trim().length > 0).map(e => e.trim()),
            availability: parseInt(availability),
            photo_url,
            updated_at: new Date().toISOString()
          })
          .eq('id', initialData.id);

        if (updateError) throw updateError;
        toast.success('Team member updated successfully');
      }

      onSuccess();
    } catch (error) {
      console.error('Error:', error);
      setError('Failed to save team member. Please try again.');
      toast.error('Failed to save team member');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Photo Upload */}
      <div className="flex flex-col items-center">
        <div className="relative">
          <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700">
            {photoPreview ? (
              <img
                src={photoPreview}
                alt="Team member photo preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Upload className="h-8 w-8 text-gray-400" />
              </div>
            )}
          </div>
          <label
            htmlFor="photo-upload"
            className="absolute bottom-0 right-0 bg-indigo-600 rounded-full p-2 cursor-pointer hover:bg-indigo-700"
          >
            <Upload className="h-4 w-4 text-white" />
          </label>
          <input
            id="photo-upload"
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="hidden"
          />
        </div>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          Upload team member photo
        </p>
      </div>

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Name
        </label>
        <input
          type="text"
          id="name"
          required
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Email
        </label>
        <input
          type="email"
          id="email"
          required
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>

      <div>
        <label htmlFor="job-title" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Job Title
        </label>
        <input
          type="text"
          id="job-title"
          required
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          value={jobTitle}
          onChange={(e) => setJobTitle(e.target.value)}
        />
      </div>
      
      <div>
        <label htmlFor="availability" className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Weekly Availability (hours)
        </label>
        <input
          type="number"
          id="availability"
          min="0"
          max="168"
          required
         className="block w-full rounded-lg shadow-sm sm:text-sm dark:text-white"
          value={availability}
          onChange={(e) => setAvailability(e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          Expertise
          <button
            type="button"
            onClick={addExpertiseField}
            className="ml-2 inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:bg-indigo-900/50 dark:text-indigo-300"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Expertise
          </button>
        </label>
        <div className="space-y-2 mt-2">
          {expertise.map((tech, index) => (
            <div key={index} className="flex items-center space-x-2">
              <input
                type="text"
                value={tech}
                onChange={(e) => updateExpertise(index, e.target.value)}
                placeholder="Enter expertise (e.g., React, Node.js, Python)"
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
              <button
                type="button"
                onClick={() => removeExpertiseField(index)}
                className="p-2 text-gray-400 hover:text-red-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                title="Remove expertise"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          {expertise.length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Click "Add Expertise" to add team member's expertise
            </p>
          )}
        </div>
      </div>


      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
          <div className="text-sm text-red-700 dark:text-red-200">{error}</div>
        </div>
      )}

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex items-center justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {loading ? (
            'Saving...'
          ) : (
            initialData ? 'Update Team Member' : 'Add Team Member'
          )}
        </button>
      </div>
    </form>
  );
}